import { Button, Popover, Tooltip } from "@mantine/core";
import {
    IconAirBalloon,
    IconPlus,
    IconUsersGroup,
    IconBuildingStore,
    IconGrowth,
    IconSquare0,
    IconRoute,
    IconCalendarCheck,
    IconCashPlus,
    IconCashMinus,
    IconBellDollar,
    IconChevronRight,
    IconPackages,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { openPayingCustomersModal } from "@/components/paying-customers-only-modal";
import ModalNewService from "./modal-new-service";
import ModalNewProduct from "./modal-new-product";
import CreateBusinessComponent from "./create-new-business-component";
import ModalNewInventoryItem from "./modal-new-inventory-item";
// import CreateOrSelectPaxComponent from "./create-new-pax-component";
import ModalNewTicket from "./modal-new-ticket";
import CardModal from "./card-modal";
import NewPaymentInModal from "./new-payment-in-modal";
import NewPaymentOutModal from "./new-payment-out-modal";
import NewPaymentRequestModal from "./new-payment-request-modal";
import NewReservationComponent from "./new-reservation-component";
import { useCreateNewServiceOrder } from "./new-service-order-component";
import { useNavigate } from "react-router-dom";
import { localUser } from "@/services/session-service";
import { isTier1Tenant } from "@/lib/format-utils";

export const CREATE_ACTION_PERMISSIONS_LIST: { key: string; labelKey: string }[] = [
    { key: 'create_reservation', labelKey: 'new_reservation' },
    { key: 'create_payment_income', labelKey: 'register_payment_from_client' },
    { key: 'create_payment_outcome', labelKey: 'register_payment_to_provider' },
    { key: 'create_payment_reminder', labelKey: 'create_payment_reminder' },
    { key: 'create_service_order', labelKey: 'new_service_order' },
    { key: 'create_business', labelKey: 'add_new_business' },
    { key: 'create_pax', labelKey: 'add_new_pax' },
    { key: 'create_service', labelKey: 'add_service' },
    { key: 'create_product', labelKey: 'add_product' },
    { key: 'create_inventory_item', labelKey: 'add_new_inventory_item' },
    { key: 'new_ticket', labelKey: 'new_ticket' },
];

interface MenuOption {
    label: string;
    permissionKey?: string;
    icon: JSX.Element;
    component?: JSX.Element | null;
    divider?: boolean;
    link?: string;
    action?: () => void;
}

export default function CreateActionsMenu({ isCollapsed = false }: { isCollapsed?: boolean }): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { createNewServiceOrder, isPending: isServiceOrderPending } = useCreateNewServiceOrder();

    const iconSize = 19;
    const iconStroke = 2;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedModal, setSelectedModal] = useState<MenuOption | null>(null);
    const [isReservationSelectorOpen, setIsReservationSelectorOpen] = useState(false);

    const user = localUser();

    const MenuOptions: MenuOption[] = [
        {
            label: t('new_reservation'),
            permissionKey: 'create_reservation',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100/80 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                    <IconCalendarCheck size={iconSize} stroke={iconStroke} />
                </div>
            ),
            action: () => setIsReservationSelectorOpen(true),
            divider: true,
        },
        {
            label: t('add_new_business'),
            permissionKey: 'create_business',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-purple-50 border border-purple-100/80 flex items-center justify-center text-purple-600 shrink-0 shadow-2xs">
                    <IconBuildingStore size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <CreateBusinessComponent />,
        },
        {
            label: t('new_service_order'),
            permissionKey: 'create_service_order',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100/80 flex items-center justify-center text-gray-600 shrink-0 shadow-2xs">
                    <IconRoute size={iconSize} stroke={iconStroke} />
                </div>
            ),
            action: () => createNewServiceOrder(),
            divider: true,
        },
        {
            label: t('register_payment_from_client'),
            permissionKey: 'create_payment_income',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100/80 flex items-center justify-center text-emerald-600 shrink-0 shadow-2xs">
                    <IconCashPlus size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <NewPaymentInModal onClose={() => setSelectedModal(null)} />,
        },
        {
            label: t('register_payment_to_provider'),
            permissionKey: 'create_payment_outcome',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100/80 flex items-center justify-center text-orange-600 shrink-0 shadow-2xs">
                    <IconCashMinus size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <NewPaymentOutModal onClose={() => setSelectedModal(null)} />,
        },
        {
            label: t('create_payment_reminder'),
            permissionKey: 'create_payment_reminder',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100/80 flex items-center justify-center text-amber-600 shrink-0 shadow-2xs">
                    <IconBellDollar size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <NewPaymentRequestModal onClose={() => setSelectedModal(null)} />,
            divider: true,
        },

        // is VERY not usefull here, only inside the reservations for now
        // {
        //     label: t('add_new_pax'),
        //     permissionKey: 'create_pax',
        //     icon: (
        //         <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-100/80 flex items-center justify-center text-teal-600 shrink-0 shadow-2xs">
        //             <IconUsersGroup size={iconSize} stroke={iconStroke} />
        //         </div>
        //     ),
        //     component: <CreateOrSelectPaxComponent onClose={() => setSelectedModal(null)} onCreate={() => setSelectedModal(null)} />,
        //     // divider: true,
        // },
        {
            label: t('add_service'),
            permissionKey: 'create_service',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-100/80 flex items-center justify-center text-sky-600 shrink-0 shadow-2xs">
                    <IconAirBalloon size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <ModalNewService onCreate={() => setSelectedModal(null)} />,
        },
        {
            label: t('add_product'),
            permissionKey: 'create_product',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100/80 flex items-center justify-center text-indigo-600 shrink-0 shadow-2xs">
                    <IconPackages size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <ModalNewProduct onCreate={() => setSelectedModal(null)} />,
        },
        {
            label: t('add_new_inventory_item'),
            permissionKey: 'create_inventory_item',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100/80 flex items-center justify-center text-orange-600 shrink-0 shadow-2xs">
                    <IconGrowth size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <ModalNewInventoryItem onCreate={() => setSelectedModal(null)} />,
            // divider: true,
        },
        {
            label: t('new_task'),
            permissionKey: 'new_ticket',
            icon: (
                <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200/80 flex items-center justify-center text-slate-700 shrink-0 shadow-2xs">
                    <IconSquare0 size={iconSize} stroke={iconStroke} />
                </div>
            ),
            component: <ModalNewTicket onCreate={() => setSelectedModal(null)} />,
        },
    ];

    const visibleMenuOptions = MenuOptions
        .filter((item) => {
            if (item.permissionKey && user?.permissions && user.permissions[item.permissionKey] === false) {
                return false;
            }
            return true;
        });

    if (visibleMenuOptions.length === 0) {
        return <></>;
    }

    const buttonContent = (
        <Button
            radius="xl"
            color="black"
            size="xs"
            onClick={() => setIsOpen(!isOpen)}
            loading={isServiceOrderPending}
            leftSection={
                <IconPlus
                    size={16}
                    stroke={2.5}
                />
            }
        >
            {t('create_action_menu')}
        </Button>
    );

    return (
        <>
            <Popover
                shadow="xl"
                radius="lg"
                closeOnClickOutside={true}
                closeOnEscape={true}
                opened={isOpen}
                onChange={setIsOpen}
                width={300}
                position="bottom-start"
            >
                {/* Popover Target */}
                <Popover.Target>
                    {isCollapsed ? (
                        <Tooltip label={t('create_action_menu')} position="right" withArrow>
                            {buttonContent}
                        </Tooltip>
                    ) : buttonContent}
                </Popover.Target>

                {/* Popover Dropdown */}
                <Popover.Dropdown p={1.5} className="rounded-2xl border border-slate-200/80 shadow-2xl overflow-hidden">
                    <div
                        className="flex flex-col gap-0.5 p-1"
                        onClick={() => setIsOpen(false)}
                    >
                        {visibleMenuOptions.map((item, index) => (
                            <div key={index}>
                                <div
                                    className="group cursor-pointer px-2.5 py-2 rounded-xl text-xs font-bold text-slate-800 hover:text-slate-900 hover:bg-slate-100/90 active:scale-[0.99] flex items-center justify-between gap-3 transition-all duration-150"
                                    onClick={() => {
                                        if (item.link) {
                                            navigate(item.link);
                                        }
                                        if (item.component) {
                                            if (item.label === t('payment_log') && !isTier1Tenant()) {
                                                openPayingCustomersModal();
                                            } else {
                                                setSelectedModal(item);
                                            }
                                        }
                                        if (item.action) {
                                            item.action();
                                            setIsOpen(false);
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        {item.icon}
                                        <span className="truncate tracking-tight">{item.label}</span>
                                    </div>
                                    <IconChevronRight size={14} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                                </div>
                                {item.divider && index < visibleMenuOptions.length - 1 && (
                                    <div className="my-1 border-t border-slate-100" />
                                )}
                            </div>
                        ))}
                    </div>
                </Popover.Dropdown>
            </Popover>

            {/* Modal */}
            <CardModal
                size="full"
                opened={!!selectedModal}
                onClose={() => setSelectedModal(null)}
                header={
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        {selectedModal?.icon}
                        {selectedModal?.label}
                    </div>
                }
                closeOnEscape={true}
            >
                {selectedModal?.component}
            </CardModal>

            {/* Reservation Client Selector Modal */}
            <NewReservationComponent
                opened={isReservationSelectorOpen}
                onClose={() => setIsReservationSelectorOpen(false)}
            />
        </>
    );
}